import type { IconName } from '../icons';
import { Icon } from './Icon';

// Segmented control — one generic primitive, three reference-derived variants, no business
// vocabulary inside (the consumer passes labels, tones and icons):
//   compact  02 / 03 one-row result selector: tonal container, equal segments, selected = solid tone
//   tiles    05 / 06 ITEM STATUS: four 80px white tiles, selected = soft tone + 2px border + count badge
//   pill     05 / 06 "Inspect | Report Draft": pill container, selected = primary solid
// Tones: the four result tones carry the Figma result colours (pass = green · defect = amber ·
// unable = orange · na = slate); primary = brand, error = red, secondary / neutral = ink. Nothing
// here knows what a result is — the consumer picks the tone.
export type SegmentTone = 'primary' | 'error' | 'secondary' | 'neutral' | 'pass' | 'defect' | 'unable' | 'na';

export interface SegmentOption<K extends string> {
  key: K;
  label: string;
  icon?: IconName;
  tone?: SegmentTone;
  count?: number;
  disabled?: boolean;
  testId?: string;
}

export function SegmentedControl<K extends string>(props: {
  options: SegmentOption<K>[];
  value: K | null;
  onChange: (key: K) => void;
  variant?: 'compact' | 'tiles' | 'pill';
  block?: boolean;
  ariaLabel?: string;
  testId?: string;
}) {
  const variant = props.variant ?? 'compact';
  const className = ['segmented', variant === 'tiles' ? 'segmented-tiles' : '', variant === 'pill' ? 'segmented-pill' : '', props.block ? 'segmented-block' : '']
    .filter((c) => c.length > 0)
    .join(' ');
  return (
    <div className={className} role="radiogroup" aria-label={props.ariaLabel} data-variant={variant} data-testid={props.testId}>
      {props.options.map((option) => {
        const selected = option.key === props.value;
        const tone = option.tone ?? 'primary';
        return (
          <button
            key={option.key}
            type="button"
            role="radio"
            aria-checked={selected}
            className={`segment segment-${tone} ${selected ? 'segment-selected' : ''}`}
            onClick={() => props.onChange(option.key)}
            disabled={option.disabled}
            data-testid={option.testId}
            data-key={option.key}
          >
            {option.icon ? <Icon name={option.icon} /> : null}
            <span className="segment-label">{option.label}</span>
            {option.count !== undefined && option.count > 0 ? <span className="count-badge">{option.count}</span> : null}
          </button>
        );
      })}
    </div>
  );
}
